import { apiErrorResponseSchema } from "@multi-chat/shared";
import { noteOrganizationRequestFailure } from "../features/organizations/organization-suspended-signal.js";

const API_BASE_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:3000").replace(
  /\/+$/,
  "",
);

export { API_BASE_URL };

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly requestId: string | undefined;

  readonly details: Readonly<Record<string, string>> | undefined;

  constructor(
    message: string,
    init: { status: number; code?: string; requestId?: string; details?: Record<string, string> },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthenticated(error: unknown): boolean {
  return isApiError(error) && (error.code === "UNAUTHENTICATED" || error.status === 401);
}

export function isBackendUnavailable(error: unknown): boolean {
  return isApiError(error) && error.status === 0;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/** Turns an API error JSON body into the error the UI catches. */
export function apiErrorFromResponseBody(body: unknown, status: number): ApiError {
  const parsed = apiErrorResponseSchema.safeParse(body);
  if (!parsed.success) {
    return new ApiError(`Request failed with status ${status}`, { status });
  }

  const details: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed.data.error)) {
    if (key === "code" || key === "message" || typeof value !== "string") continue;
    details[key] = value;
  }
  return new ApiError(parsed.data.error.message, {
    status,
    code: parsed.data.error.code,
    requestId: parsed.data.requestId,
    ...(Object.keys(details).length > 0 ? { details } : {}),
  });
}

function throwApiError(body: unknown, status: number, path: string): never {
  const error = apiErrorFromResponseBody(body, status);
  if (error.code) {
    noteOrganizationRequestFailure(path, error.code);
  }
  throw error;
}

async function parseErrorResponse(response: Response, path: string): Promise<never> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON body; fall through to generic error.
  }

  throwApiError(body, response.status, path);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, headers } = options;
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: "include",
      headers: {
        accept: "application/json",
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    // Network error / DNS failure / CORS — the backend is unreachable.
    throw new ApiError(
      error instanceof Error && error.message
        ? `Cannot reach the server: ${error.message}`
        : "Cannot reach the server",
      { status: 0 },
    );
  }

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiGetBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      credentials: "include",
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw new ApiError(
      error instanceof Error && error.message
        ? `Cannot reach the server: ${error.message}`
        : "Cannot reach the server",
      { status: 0 },
    );
  }

  if (!response.ok) {
    await parseErrorResponse(response, path);
  }

  return await response.blob();
}

export async function apiDownload(
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; filename: string | null }> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      credentials: "include",
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiError("Cannot reach the server", { status: 0 });
  }
  if (!response.ok) {
    await parseErrorResponse(response, path);
  }
  const match = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "");
  return { blob: await response.blob(), filename: match?.[1] ?? null };
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { signal });
}

export async function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "POST", body, signal });
}

export async function apiPostFormData<T>(
  path: string,
  formData: FormData,
  signal?: AbortSignal,
): Promise<T> {
  return apiPostFormDataWithProgress(path, formData, { signal });
}

export async function apiPostFormDataWithProgress<T>(
  path: string,
  formData: FormData,
  options: {
    signal?: AbortSignal;
    onProgress?: (percent: number) => void;
    headers?: Record<string, string>;
  } = {},
): Promise<T> {
  const { signal, onProgress, headers } = options;
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("accept", "application/json");
    if (headers) {
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }
    }

    const abort = () => {
      xhr.abort();
    };
    signal?.addEventListener("abort", abort);

    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable || event.total <= 0) return;
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onload = () => {
      signal?.removeEventListener("abort", abort);
      void (async () => {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (xhr.status === 204) {
              resolve(undefined as T);
              return;
            }
            resolve(JSON.parse(xhr.responseText) as T);
            return;
          }
          let body: unknown = null;
          try {
            body = JSON.parse(xhr.responseText);
          } catch {
            body = null;
          }
          throwApiError(body, xhr.status, path);
        } catch (error) {
          reject(error);
        }
      })();
    };

    xhr.onerror = () => {
      signal?.removeEventListener("abort", abort);
      reject(
        new ApiError("Cannot reach the server", {
          status: 0,
        }),
      );
    };

    xhr.onabort = () => {
      signal?.removeEventListener("abort", abort);
      reject(new DOMException("The operation was aborted.", "AbortError"));
    };

    xhr.send(formData);
  });
}

export async function apiPatch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "PATCH", body, signal });
}

export async function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "PUT", body, signal });
}

export async function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { method: "DELETE", signal });
}
